/**
 * Utility functions for handling contact URL parameters
 */

/**
 * Determines the type of each parameter in the URL
 * @param params Object containing URL parameters
 * @returns Object with identified email, phone, and contactId
 */
export const determineParameterTypes = (params: Record<string, string | undefined>) => {
  let email: string | undefined;
  let phone: string | undefined;
  let contactId: string | undefined;

  // Skip the orgId parameter and process the rest
  Object.entries(params).forEach(([key, value]) => {
    if (key === 'orgId' || !value) return;

    // Check if it's an email (contains '@')
    if (value.includes('@')) {
      email = value;
    }
    // Check if it's a phone number (contains mostly digits)
    else if (/^[0-9+\-() ]*$/.test(value)) {
      phone = value;
    }
    // If it's neither email nor phone, assume it's a contactId
    else {
      contactId = value;
    }
  });

  return {
    email,
    phone,
    contactId,
    // Helper functions to check if values are valid (not 'na')
    hasEmail: !!email && email.toLowerCase() !== 'na',
    hasPhone: !!phone && phone.toLowerCase() !== 'na',
    hasContactId: !!contactId && contactId.toLowerCase() !== 'na',
  };
};

/**
 * Prepares parameters for API calls by handling 'na' values
 * @param params Object containing identified parameters
 * @returns Object with cleaned parameters ready for API calls
 */
export const prepareContactParams = (params: {
  email?: string;
  phone?: string;
  contactId?: string;
}) => {
  return {
    emailParam: params.email && params.email.toLowerCase() !== 'na' ? params.email : '',
    phoneParam: params.phone && params.phone.toLowerCase() !== 'na' ? params.phone : '',
    contactIdParam:
      params.contactId && params.contactId.toLowerCase() !== 'na' ? params.contactId : '',
  };
};

/**
 * Generates a contact URL based on contact ID
 * @param contact Contact object with id
 * @returns URL string with only the contact ID
 */
export const generateContactUrl = (contact: {
  id: string;
  email?: string;
  phoneNumber?: string;
}) => {
  return `/contact/${contact.id}`;
};
